import { UUID } from 'crypto'
import { throwIfProfileRegenerating } from './profileRegenError'
import {
  OrgData,
  OrgDocResponse,
  OrgsCreate,
  OrgsGeneralInfo,
  ProfileGenerationJobResponse,
  ProfileRegenTriggerResponse,
  ProfileRevisionSummary,
  ProfileRevisionDetail,
} from '../models/Orgs'

const serverURL = import.meta.env.VITE_API_URL

interface UpdateOrgFn {
  token: string
  editedOrg: OrgData
}

export interface DuplicateOrgNameDetail {
  orgName: string
  orgUuid: string
  alreadyMember: boolean
}

// Thrown by useAddOrg/useUpdateOrg instead of a plain Error so callers can
// detect a duplicate-name 409 by type, not by matching error copy — the
// backend's message text isn't a stable contract to compare against.
export class DuplicateOrgNameError extends Error {
  duplicate: DuplicateOrgNameDetail

  constructor(message: string, duplicate: DuplicateOrgNameDetail) {
    super(message)
    this.name = 'DuplicateOrgNameError'
    this.duplicate = duplicate
  }
}

function parseDuplicateOrgNameDetail(
  errBody: unknown,
): DuplicateOrgNameDetail | null {
  const detail = (errBody as { detail?: unknown } | null)?.detail
  if (
    !detail ||
    typeof detail !== 'object' ||
    (detail as { code?: unknown }).code !== 'duplicate_org_name'
  ) {
    return null
  }
  const d = detail as {
    org_name?: unknown
    org_uuid?: unknown
    already_member?: unknown
  }
  // Validate every field individually rather than trusting the cast — a
  // malformed or partial response (code present, fields missing/wrong type)
  // must fall back to the generic message, not flow "undefined" into the UI
  // or a broken Go-to-org button with no uuid to switch to.
  if (
    typeof d.org_name !== 'string' ||
    typeof d.org_uuid !== 'string' ||
    typeof d.already_member !== 'boolean'
  ) {
    return null
  }
  return { orgName: d.org_name, orgUuid: d.org_uuid, alreadyMember: d.already_member }
}

export interface SavePreferenceItem {
  preference_text: string
  need_id?: number
}

export async function saveMatchPreferences(
  orgUuid: string,
  preferences: SavePreferenceItem[],
  token: string,
): Promise<{ success: boolean }> {
  const response = await fetch(`${serverURL}/orgs/${orgUuid}/preferences`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({ preferences }),
  })

  if (!response.ok) {
    throw new Error('Failed to save preferences')
  }

  return response.json()
}

export interface OrgCreateResponse {
  org_uuid: string
  user_uuid: string
  message: string
}

export async function addOrg(
  token: string,
  org: OrgsCreate,
  general_info: OrgsGeneralInfo,
): Promise<
  | { createdOrg: OrgCreateResponse }
  | { error: string; duplicate?: DuplicateOrgNameDetail }
> {
  const orgData = {
    name: org.name,
    charities_number: org.charitiesNumber,
    org_desc: org.description, // Backend expects org_desc, not description
    regions: org.region_ids, // Backend expects regions, not region_ids
    website: org.website,
    iwi: org.iwi,
    accepts_gambling_proceeds: org.accepts_gambling_proceeds,
    accepts_international: org.accepts_international,
    general_info: general_info,
  }

  const response = await fetch(`${serverURL}/orgs/`, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(orgData),
  })

  if (response.status === 201) {
    // Try to parse as JSON first, fall back to text if not JSON
    let resBody
    const contentType = response.headers.get('content-type')
    if (contentType && contentType.includes('application/json')) {
      resBody = await response.json()
    } else {
      resBody = await response.text()
    }
    return { createdOrg: resBody }
  } else if (response.status === 409) {
    const errBody = await response.json().catch(() => null)
    const duplicate = parseDuplicateOrgNameDetail(errBody) ?? undefined
    return {
      error: 'Organisation already exists with this name',
      duplicate,
    }
  } else {
    return { error: 'Sorry something went wrong' }
  }
}
export async function fetchUserOrgsById(
  token: string,
  orgUuids: UUID[],
): Promise<(OrgData | { org_uuid: UUID; error: string })[]> {
  const orgResponses = await Promise.all(
    orgUuids.map(async (org_uuid) => {
      try {
        const response = await fetch(`${serverURL}/orgs/${org_uuid}`, {
          method: 'GET',
          headers: {
            Authorization: `Bearer ${token}`,
          },
        })

        if (response.ok) {
          const data = await response.json()
          return {
            uuid: data.uuid,
            name: data.name,
            charities_number: data.charities_number,
            description: data.description,
            profile_text: data.profile_text,
            profile_regen_status: data.profile_regen_status,
            region_ids: data.region_ids,
            early_access: data.early_access,
            active: data.active,
            status: data.status,
            membership: data.membership,
            membership_until: data.membership_until,
            tier: data.tier,
            payment_type: data.payment_type,
            billing_frequency: data.billing_frequency,
            iwi: data.iwi,
            accepts_gambling_proceeds: data.accepts_gambling_proceeds,
            accepts_international: data.accepts_international,
            general_info: data.general_info || null,
            website: data.website || null,
            max_total_scans: data.max_total_scans,
            need_only_context: data.need_only_context,
            sponsorship_letter: data.sponsorship_letter,
            pause_resume_enabled: data.pause_resume_enabled,
            subscription_status: data.subscription_status,
            pause_until_date: data.pause_until_date,
            can_set_preferences: data.can_set_preferences,
            is_child_of: data.is_child_of,
            parent_org_name: data.parent_org_name,
            is_annual_subscriber: data.is_annual_subscriber === true,
          }
        }

        return {
          org_uuid,
          error: `Error fetching organisation with ID ${org_uuid}: ${response.statusText}`,
        }
      } catch (error) {
        return {
          org_uuid,
          error: `Error fetching organisation with ID ${org_uuid}: ${error}`,
        }
      }
    }),
  )

  return orgResponses
}

export async function updateOrg({
  token,
  editedOrg,
}: UpdateOrgFn): Promise<
  { editedOrg: OrgData } | { error: string; duplicate?: DuplicateOrgNameDetail }
> {
  const response = await fetch(`${serverURL}/orgs/${editedOrg.uuid}`, {
    method: 'PATCH',
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      name: editedOrg.name,
      charities_number: editedOrg.charities_number,
      org_desc: editedOrg.description, // Backend expects org_desc, not description
      regions: editedOrg.region_ids, // Backend expects regions, not region_ids
      iwi: editedOrg.iwi,
      accepts_gambling_proceeds: editedOrg.accepts_gambling_proceeds,
      accepts_international: editedOrg.accepts_international,
      general_info: editedOrg.general_info,
      tier: editedOrg.tier,
    }),
  })

  if (response.status === 201 || response.status === 200) {
    const resBody = await response.json()
    return { editedOrg: resBody }
  } else if (response.status === 409) {
    const errBody = await response.json().catch(() => null)
    const duplicate = parseDuplicateOrgNameDetail(errBody) ?? undefined
    return {
      error: 'Organisation already exists with this name',
      duplicate,
    }
  } else {
    return { error: 'Sorry something went wrong' }
  }
}

export async function fetchOrgDocs(
  token: string,
  orgUuid: UUID,
): Promise<OrgDocResponse[]> {
  const response = await fetch(`${serverURL}/orgs/${orgUuid}/docs`, {
    method: 'GET',
    headers: {
      Authorization: `Bearer ${token}`,
    },
  })

  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`)
  }

  const docs = await response.json()
  return docs
}

export async function generateProfile(
  token: string,
  org_uuid: UUID,
): Promise<ProfileGenerationJobResponse> {
  const response = await fetch(
    `${serverURL}/orgs/${org_uuid}/generate-profile`,
    {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`,
      },
    },
  )

  if (!response.ok) {
    await throwIfProfileRegenerating(response)
    throw new Error('Failed to generate profile')
  }

  const data: ProfileGenerationJobResponse = await response.json()
  return data
}

// Kick off a background profile regeneration that auto-saves the result. Returns
// a job_id to track progress (null if a regen was already running). Used by the
// manual "Update profile" retry; uploads/description edits enqueue server-side.
export async function triggerProfileRegen(
  token: string,
  org_uuid: UUID,
): Promise<ProfileRegenTriggerResponse> {
  const response = await fetch(
    `${serverURL}/orgs/${org_uuid}/regenerate-profile`,
    {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`,
      },
    },
  )

  if (!response.ok) {
    // An already-running regen returns the 409 block shape — surface it as the
    // typed error so callers route it through the shared block dialog (and the
    // status poll finishes the update) instead of showing a start failure.
    await throwIfProfileRegenerating(response)
    throw new Error('Failed to start profile update')
  }

  const data: ProfileRegenTriggerResponse = await response.json()
  return data
}

export async function listProfileRevisions(
  token: string,
  org_uuid: UUID,
): Promise<ProfileRevisionSummary[]> {
  const response = await fetch(
    `${serverURL}/orgs/${org_uuid}/profile-revisions`,
    { headers: { Authorization: `Bearer ${token}` } },
  )
  if (!response.ok) {
    throw new Error('Failed to load profile history')
  }
  return response.json()
}

export async function getProfileRevision(
  token: string,
  org_uuid: UUID,
  version: number,
): Promise<ProfileRevisionDetail> {
  const response = await fetch(
    `${serverURL}/orgs/${org_uuid}/profile-revisions/${version}`,
    { headers: { Authorization: `Bearer ${token}` } },
  )
  if (!response.ok) {
    throw new Error('Failed to load this version')
  }
  return response.json()
}

export async function updateProfileText(
  token: string,
  org_uuid: UUID,
  profileText: string,
  force?: boolean,
) {
  const response = await fetch(`${serverURL}/orgs/${org_uuid}/profile-text`, {
    method: 'PATCH',
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ profile_text: profileText, force }),
  })

  if (response.status === 200) {
    const resBody = await response.json()
    return resBody
  } else {
    await throwIfProfileRegenerating(response)
    return { error: 'Sorry something went wrong' }
  }
}
export async function appendDescriptionAndGenerate(
  token: string,
  org_uuid: UUID,
  additionalText: string,
): Promise<ProfileGenerationJobResponse> {
  const response = await fetch(
    `${serverURL}/orgs/${org_uuid}/append-description-and-generate`,
    {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ additional_text: additionalText }),
    },
  )
  if (!response.ok) {
    await throwIfProfileRegenerating(response)
    throw new Error('Failed to append description and generate profile')
  }
  return await response.json()
}

export interface OrgAccountDetails {
  uuid: string
  membership: string
  membership_until: string | null
  tier: number | null
  payment_type: string | null
  billing_frequency: string | null
  billing_amount: number | null
  has_sub_orgs: boolean
  billing_legal_name: string | null
  billing_email: string | null
  billing_contact_name: string | null
  billing_contact_phone: string | null
  billing_address_line1: string | null
  billing_address_line2: string | null
  billing_city: string | null
  billing_state: string | null
  billing_postal_code: string | null
  billing_country: string | null
  tax_registration_number: string | null
  billing_notes: string | null
  charity_tier: number | null
  xero_contact_id: string | null
  xero_repeating_invoice_id: string | null
  repeating_invoice_template_name: string | null
  repeating_invoice_status: string | null
}

export interface OrgPreference {
  id: number
  org_uuid: string
  need_id: number | null
  preference_text: string
  created_at: string
  updated_at: string
}

export async function fetchOrgPreferences(
  orgUuid: string,
  token: string,
  needId?: number,
): Promise<OrgPreference[]> {
  const params = needId != null ? `?need_id=${needId}` : ''
  const response = await fetch(
    `${serverURL}/orgs/${orgUuid}/preferences${params}`,
    {
      method: 'GET',
      headers: {
        Authorization: `Bearer ${token}`,
      },
    },
  )

  if (!response.ok) {
    throw new Error('Failed to fetch preferences')
  }

  return response.json()
}

export async function updateOrgPreference(
  orgUuid: string,
  preferenceId: number,
  preferenceText: string,
  token: string,
): Promise<{ message: string }> {
  const response = await fetch(
    `${serverURL}/orgs/${orgUuid}/preferences/${preferenceId}`,
    {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({ preference_text: preferenceText }),
    },
  )

  if (!response.ok) {
    throw new Error('Failed to update preference')
  }

  return response.json()
}

export async function deleteOrgPreference(
  orgUuid: string,
  preferenceId: number,
  token: string,
): Promise<void> {
  const response = await fetch(
    `${serverURL}/orgs/${orgUuid}/preferences/${preferenceId}`,
    {
      method: 'DELETE',
      headers: {
        Authorization: `Bearer ${token}`,
      },
    },
  )

  if (!response.ok) {
    throw new Error('Failed to delete preference')
  }
}

export async function fetchOrgAccountDetails(
  token: string,
  orgUuid: UUID,
): Promise<{ accountDetails: OrgAccountDetails } | { error: string }> {
  try {
    const response = await fetch(`${serverURL}/orgs/${orgUuid}/account`, {
      method: 'GET',
      headers: {
        Authorization: `Bearer ${token}`,
      },
    })

    if (response.ok) {
      const accountDetails = await response.json()
      return { accountDetails }
    } else {
      const errorResponse = await response.json()
      return {
        error:
          errorResponse.detail ||
          'Failed to fetch organisation account details',
      }
    }
  } catch (error) {
    return {
      error: `Error fetching organisation account details: ${error}`,
    }
  }
}
